import { ref, watch, unref, type MaybeRef } from 'vue';
import { useStrapiGet } from '../useStrapiApi';
import type { StrapiPlurals } from '../useStrapiApi';

function normalizeOption(item: unknown): Record<string, unknown> | null {
  if (!item || typeof item !== 'object') return null;

  const record = item as Record<string, unknown>;

  // Already a flat option-like object
  if ('id' in record && !('attributes' in record) && !('data' in record)) {
    return record;
  }

  // Standard Strapi shape: { id, attributes: {...} }
  if (
    'id' in record &&
    typeof record.attributes === 'object' &&
    record.attributes
  ) {
    return {
      id: record.id,
      ...(record.attributes as Record<string, unknown>),
    };
  }

  // Wrapped shape: { data: { id, attributes } }
  if (typeof record.data === 'object' && record.data) {
    return normalizeOption(record.data);
  }

  return record;
}

export const useSelectOptions = (
  pluralApiId: StrapiPlurals,
  label: string,
  filters?: MaybeRef<Record<string, unknown>>,
) => {
  const search = ref('');
  const loading = ref(false);
  const options = ref<Record<string, unknown>[]>([]);

  const { data, execute, error } = useStrapiGet(
    pluralApiId,
    {},
    {
      suppressError: true,
    },
  );

  const fetchOptions = async (searchValue: string) => {
    console.log({ searchValue });
    loading.value = true;

    const currentFilters = unref(filters) || {};

    await execute({
      params: {
        fields: [label],
        filters: {
          [label]: {
            $containsi: searchValue,
          },

          ...currentFilters,
        },
        sort: [`${label}:asc`],
        pagination: {
          page: 1,
          pageSize: 10,
        },
      },
    });

    if (error.value) {
      console.error(
        `useSelectOptions: Error fetching options for ${pluralApiId}:`,
        error.value,
      );
      options.value = [];
      loading.value = false;
      return;
    }

    const rawOptions =
      (data?.value?.data?.data as unknown[]) ??
      (data?.value?.data as unknown[]) ??
      [];

    options.value = rawOptions
      .map((item) => normalizeOption(item))
      .filter((item): item is Record<string, unknown> => !!item);

    loading.value = false;
  };

  // Watch search input
  watch(search, fetchOptions);

  // Watch filters (if reactive)
  if (filters) {
    watch(
      () => unref(filters),
      () => {
        void fetchOptions(search.value);
      },
      { deep: true },
    );
  }

  return {
    search,
    options,
    loading,
  };
};
